<template>
  <div id="app" style="height: 100vh">
    <template v-if="$store.state.app.initReady">
      <b-container fluid class="h-100 p-0">
        <div class="d-flex h-100">
          <!-- Mobile Backdrop -->
          <div 
            v-if="isMobile && menuActive" 
            class="mobile-backdrop"
            @click="menuActive = false"
          ></div>

          <!-- Sidebar -->
          <div 
            id="menu-col" 
            :class="{
              'active': menuActive, 
              'collapsed': !menuActive,
              'mobile': isMobile
            }"
          >
            <Menu ref="menu" class="h-100 pt-3"/>
          </div>

          <!-- Main Content -->
          <div id="content-col" class="flex-grow-1">
            <Bar 
              :is-media-page="isMediaPage" 
              :active-page-title="activePageTitle" 
              ref="bar"
              @toggle-menu="toggleMenu" 
            />
            <b-overlay :show="$store.state.app.overlay" rounded="sm" class="h-100">
              <div class="mx-4 content-wrapper">
                <router-view/>
              </div>
            </b-overlay>
          </div>
        </div>
      </b-container>
      <ErrorDialog ref='appErrorDialog'/>
    </template>

    <template v-else-if="loggedInChecked && !$store.state.session.loggedIn">
        <Login/>
    </template>

    <template v-else>
        <!-- Display nothing, we don't know if user is logged in yet -->
        <div class="loader-holder">
            <div class="lds-dual-ring"></div>
        </div>
    </template>

    <transition name="fade">
      <footer v-if="alert.dismissCountdown > 0" class="footer">
        <b-alert :show="alert.dismissCountdown" dismissible :variant="alert.variant" @dismissed="alert.dismissCountdown = 0">
          {{ alert.content }}
        </b-alert>
      </footer>
    </transition>

  </div>
</template>

<script>
import Bar from './components/Bar.vue'
import Menu from './components/Menu.vue'
import ErrorDialog from './components/ErrorDialog.vue'
import Login from './views/Login.vue'

export default {
  name: 'app',

  components: {
    Bar,
    Menu,
    ErrorDialog,
    Login
  },

  data: function() {
    return {
      menuActive: true,
      activePageTitle: "",
      alert: {
        content: "",
        variant: "succes",
        dismissCountdown: 0,

      },
      alertDefaults: {
        dismissCountdown: 4,
        variant: "info"
      },
      isMediaPage: false,
      windowWidth: window.innerWidth
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.session.loggedIn
    },
    loggedInChecked() {
      return this.$store.state.session.loggedInChecked;
    },
    isMobile() {
      return this.windowWidth < 768; // Matching $mobile-breakpoint
    }
  },

  watch: {
    loggedIn(value) {
      if(value) {
        this.$store.dispatch('fetchAll')
      }
    },
    // Auto-close menu on route change on mobile
    $route() {
      if (this.isMobile) {
        this.menuActive = false;
      }
    }
  },

  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
      if (this.windowWidth >= 768 && !this.menuActive) {
        this.menuActive = true;
      }
    },

    toggleMenu() {
      this.menuActive = !this.menuActive;
    },

    showMenu(show) {
      this.menuActive = show
    },

    showAlert(content, variant, countdown) {
      this.alert = {
        content: content,
        variant: variant || this.alertDefaults.variant,
        dismissCountdown: countdown || this.alertDefaults.dismissCountdown
      }
    },

    showErrorDialog(body, header) {
      this.$refs.appErrorDialog.show(body, header)
    },

    setActivePageTitle(title, isMediaPage) {
      this.activePageTitle = title
      this.isMediaPage = isMediaPage
    },
  },

  created() {
      this.$store.dispatch('session/fetchUser')
  },

  mounted() {
    this.$root.$on('menu-expand', (active) => {
      this.menuActive = active
    })
    
    window.addEventListener('resize', this.handleResize);
    this.handleResize(); // Init
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }
}
</script>


<style lang="scss">
@import './custom.scss';

#app {
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #E5E9EC;
  scrollbar-width: thin;
  overflow: hidden; /* Prevent body scroll */
}

#menu-col {
  width: $sidebar-width;
  height: 100vh;
  overflow: hidden;
  transition: width 0.3s ease, transform 0.3s ease;
  background-color: transparent; /* Assuming menu has its own bg or transparent */
  flex-shrink: 0;
  z-index: 100;

  &.collapsed {
    width: $sidebar-collapsed-width;
  }

  /* Mobile Styles */
  &.mobile {
    position: fixed;
    top: 0;
    left: 0;
    width: $sidebar-width !important; /* Always full width when open on mobile */
    transform: translateX(-100%);
    background-color: #0D1B2B; /* Needs background since it overlays */
    box-shadow: 5px 0 15px rgba(0,0,0,0.5);

    &.active {
      transform: translateX(0);
    }
  }
}

.mobile-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0,0,0,0.5);
  z-index: 99;
  backdrop-filter: blur(2px);
}

#content-col {
  overflow-y: auto; /* Scroll inside content only */
  height: 100vh;
  padding: 0;
  background: transparent radial-gradient(closest-side at 47% 48%, #1B3B5B 0%, #072034 100%) 0% 0% no-repeat padding-box;
  box-shadow: -3px 3px 10px #00000033;
  opacity: 1;
  position: relative;
}

.content-wrapper {
  /* Ensure content has some breathing room */
  padding-bottom: 2rem;
}
/* From https://loading.io/css/, under CC0 license so free to use: */
.loader-holder {
  /* Center the loader both horizontally and vertically */
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
}
.lds-dual-ring {
  display: inline-block;
  width: 80px;
  height: 80px;
}
.lds-dual-ring:after {
  content: " ";
  display: block;
  width: 64px;
  height: 64px;
  margin: 8px;
  border-radius: 50%;
  border: 6px solid #fff;
  border-color: #fff transparent #fff transparent;
  animation: lds-dual-ring 1.2s linear infinite;
}
@keyframes lds-dual-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

</style>
